import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { InboxItem } from './cp-inbox-item-card.js';

interface SharedProject {
  id: number;
  pseudo_project_id: number | null;
  project_id: number | null;
  name: string;
  type: 'pseudo_project' | 'project';
}

const PRIORITY_OPTIONS = ['critical', 'high', 'medium', 'low'];

@customElement('cp-inbox-edit-modal')
export class CpInboxEditModal extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Boolean }) open = false;
  @property({ type: Object }) item: InboxItem | null = null;

  @state() private _title = '';
  @state() private _description = '';
  @state() private _priority = 'medium';
  @state() private _targetProjectKey = '';
  @state() private _projects: SharedProject[] = [];
  @state() private _loadingProjects = false;
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _titleError = '';

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open && this.item) {
      this._resetForm();
      this._loadProjects();
    }
  }

  private _resetForm(): void {
    if (!this.item) return;
    this._title = this.item.title;
    this._description = this.item.description ?? '';
    this._priority = this.item.priority || 'medium';
    if (this.item.target_project_id !== null) {
      this._targetProjectKey = `project:${this.item.target_project_id}`;
    } else if (this.item.target_pseudo_project_id !== null) {
      this._targetProjectKey = `pseudo:${this.item.target_pseudo_project_id}`;
    } else {
      this._targetProjectKey = '';
    }
    this._saving = false;
    this._error = '';
    this._titleError = '';
  }

  private async _loadProjects(): Promise<void> {
    this._loadingProjects = true;
    try {
      const res = await fetch('/api/external-agent/shared-projects', { credentials: 'include' });
      if (!res.ok) throw new Error('Fehler beim Laden der Projekte');
      this._projects = await res.json() as SharedProject[];
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Projekte konnten nicht geladen werden';
    } finally {
      this._loadingProjects = false;
    }
  }

  private _close(): void {
    this.dispatchEvent(new CustomEvent('modal-closed', { bubbles: true, composed: true }));
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) {
      this._close();
    }
  };

  private _onBackdropClick(e: Event): void {
    if (e.target === e.currentTarget) {
      this._close();
    }
  }

  private _onTitleInput(e: Event): void {
    this._title = (e.target as HTMLInputElement).value;
  }

  private _onTitleBlur(): void {
    if (!this._title.trim()) {
      this._titleError = 'Titel ist erforderlich';
    } else {
      this._titleError = '';
    }
  }

  private _onDescriptionInput(e: Event): void {
    this._description = (e.target as HTMLTextAreaElement).value;
  }

  private _onPriorityChange(e: Event): void {
    this._priority = (e.target as HTMLSelectElement).value;
  }

  private _onProjectChange(e: Event): void {
    this._targetProjectKey = (e.target as HTMLSelectElement).value;
  }

  private _parseProjectKey(): { target_pseudo_project_id: number | null; target_project_id: number | null } {
    if (!this._targetProjectKey) {
      return { target_pseudo_project_id: null, target_project_id: null };
    }
    const [type, idStr] = this._targetProjectKey.split(':');
    const id = Number(idStr);
    if (type === 'project') {
      return { target_pseudo_project_id: null, target_project_id: id };
    }
    return { target_pseudo_project_id: id, target_project_id: null };
  }

  private async _onSave(): Promise<void> {
    if (!this.item || this._saving) return;

    const trimmedTitle = this._title.trim();
    if (!trimmedTitle) {
      this._titleError = 'Titel ist erforderlich';
      return;
    }

    this._saving = true;
    this._error = '';
    this._titleError = '';

    try {
      const body = {
        title: trimmedTitle,
        description: this._description.trim() || null,
        ...this._parseProjectKey(),
        priority: this._priority,
      };

      const res = await fetch(`/api/inbox/${this.item.id}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Speichern fehlgeschlagen' })) as { error: string };
        throw new Error(data.error || 'Speichern fehlgeschlagen');
      }

      const updatedItem = await res.json() as InboxItem;

      this.dispatchEvent(new CustomEvent('item-accepted-with-edits', {
        detail: { item: updatedItem },
        bubbles: true,
        composed: true,
      }));

      this._close();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Speichern fehlgeschlagen';
    } finally {
      this._saving = false;
    }
  }

  render() {
    if (!this.open || !this.item) return nothing;

    return html`
      <div class="cp-inbox-edit-modal__overlay" @click=${this._onBackdropClick}>
        <div class="cp-inbox-edit-modal__dialog">
          <div class="cp-inbox-edit-modal__header">
            <h2 class="cp-inbox-edit-modal__heading">Item bearbeiten & annehmen</h2>
            <button class="cp-inbox-edit-modal__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-inbox-edit-modal__body">
            <div class="cp-inbox-edit-modal__field">
              <label class="cp-inbox-edit-modal__label">Titel *</label>
              <input
                class="cp-inbox-edit-modal__input ${this._titleError ? 'cp-inbox-edit-modal__input--error' : ''}"
                type="text"
                .value=${this._title}
                @input=${this._onTitleInput}
                @blur=${this._onTitleBlur}
                placeholder="Titel eingeben"
              />
              ${this._titleError ? html`
                <span class="cp-inbox-edit-modal__field-error">${this._titleError}</span>
              ` : nothing}
            </div>

            <div class="cp-inbox-edit-modal__field">
              <label class="cp-inbox-edit-modal__label">Beschreibung</label>
              <textarea
                class="cp-inbox-edit-modal__textarea"
                rows="3"
                .value=${this._description}
                @input=${this._onDescriptionInput}
                placeholder="Optionale Beschreibung"
              ></textarea>
            </div>

            <div class="cp-inbox-edit-modal__row">
              <div class="cp-inbox-edit-modal__field cp-inbox-edit-modal__field--half">
                <label class="cp-inbox-edit-modal__label">Priorität</label>
                <select
                  class="cp-inbox-edit-modal__select"
                  .value=${this._priority}
                  @change=${this._onPriorityChange}
                >
                  ${PRIORITY_OPTIONS.map(p => html`
                    <option value=${p} ?selected=${this._priority === p}>${p}</option>
                  `)}
                </select>
              </div>

              <div class="cp-inbox-edit-modal__field cp-inbox-edit-modal__field--half">
                <label class="cp-inbox-edit-modal__label">Projekt</label>
                <select
                  class="cp-inbox-edit-modal__select"
                  @change=${this._onProjectChange}
                  ?disabled=${this._loadingProjects}
                >
                  <option value="" ?selected=${this._targetProjectKey === ''}>
                    ${this._loadingProjects ? 'Lade Projekte…' : '— Kein Projekt —'}
                  </option>
                  ${this._projects.map(p => {
                    const key = p.type === 'project'
                      ? `project:${p.project_id}`
                      : `pseudo:${p.pseudo_project_id}`;
                    return html`
                      <option
                        value=${key}
                        ?selected=${this._targetProjectKey === key}
                      >${p.name}</option>
                    `;
                  })}
                </select>
              </div>
            </div>

            ${this._error ? html`
              <div class="cp-inbox-edit-modal__error">${this._error}</div>
            ` : nothing}
          </div>

          <div class="cp-inbox-edit-modal__footer">
            <button
              class="cp-inbox-edit-modal__btn cp-inbox-edit-modal__btn--cancel"
              @click=${this._close}
            >Abbrechen</button>
            <button
              class="cp-inbox-edit-modal__btn cp-inbox-edit-modal__btn--save"
              ?disabled=${this._saving}
              @click=${this._onSave}
            >${this._saving ? 'Speichern…' : 'Speichern & Annehmen'}</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-inbox-edit-modal': CpInboxEditModal;
  }
}
